import React, { useMemo } from 'react'
import { css, Global } from '@emotion/core'
import { useFeatureFlag } from '@fs/zion-flags'
import PageAssembler from '../components/PageAssembler/PageAssembler'
import ChineseSurnamePage from './ChineseSurnamePage/ChineseSurnamePage'
import { useMemoryBook } from '../providers/MemoryBookProvider'
import { newPageState } from '../providers/MemoryBooksReducer'
import TreeDesignsPage from './TreeDesignsPage/TreeDesignsPage'
import FrontCoverPage from './FrontCoverPage/FrontCoverPage'
import BackPage from './BackPage/BackPage'
import GlobalSurnamePage from './GlobalSurnamePage/GlobalSurnamePage'
import PlaceholderSurnamePage from './PlaceholderSurnamePage/PlaceholderSurnamePage'
import useIsSafari from '../hooks/useIsSafari/useIsSafari'
import {
  FRONT_COVER_PAGE_IDS,
  BACK_COVER_PAGE_ID,
  SURNAME_PAGE_ID as CHINESE_SURNAME_PAGE_ID,
  GLOBAL_SURNAME_PAGE_ID,
  TREE_DESIGNS_PAGE_ID,
} from '../constants/templateIds'
import PrintStickerLayer from '../components/StickerLayer/PrintStickerLayer'
import FreeformImagePicker from '../components/FreeformImagePicker/FreeformImagePicker'
import FreeformTextPicker from '../components/FreeformTextPicker/FreeformTextPicker'
import FreeformTextBox from '../components/FreeformTextBox/FreeformTextBox'
import { BACKGROUND_OPTIONS_BY_ID } from '../constants/backgroundOptions'

// Flexible page dimensions - adapts to letter or A4 paper size

const PAGE_PADDING_PX = 60
const PAGE_PADDING_IN = PAGE_PADDING_PX / 96

const pageCSS = css`
  box-sizing: border-box;
  width: 100%;
  height: 90vh;
  padding: ${PAGE_PADDING_IN}in;
  margin: 0 auto;
  background-color: white;
  position: relative;
  page-break-after: avoid;
`

const safariPageCSS = css`
  /* Safari uses flexible dimensions due to 72 DPI rendering */
  width: 100% !important;
  height: auto !important;
  padding: 5.6% 11.9% !important;
  margin-top: 50px;
`
const pageContainerCSS = css`
  display: block;
  background-color: transparent;
`

const baseGlobalPrintStyles = css`
  @media all {
    .page-break {
      display: none;
    }
  }

  @media print {
    html,
    body {
      height: initial !important;
      overflow: initial !important;
      margin: 0;
      padding: 0;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      font-size: 18pt;
    }

  * {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .page-break {
    display: block;
    height: 0;
    margin: 0;
    padding: 0;
    page-break-before: always;
  }
`

// Flexible page size - adapts to user's paper selection (letter or A4)
const pageSizeStyles = css`
  @page {
    size: auto;
    margin: 0;
  }
`

const getBackgroundImageSrc = (backgroundId) => {
  if (backgroundId === undefined || backgroundId === null) {
    return null
  }

  const mappedOption = BACKGROUND_OPTIONS_BY_ID[backgroundId]
  if (mappedOption) {
    return mappedOption.printSrc
  }

  return null
}

function Page({ assemblyInstructions, templateId, pageNumber, stickers, customLayoutElements, backgroundId }) {
  const isSafari = useIsSafari()
  const backgroundImageSrc = getBackgroundImageSrc(backgroundId)

  const pageCSStyles = useMemo(() => {
    const styles = [pageCSS]
    if (isSafari) {
      styles.push(safariPageCSS)
    }

    if (backgroundImageSrc && !isSafari) {
      styles.push(css`
        background-image: url('${backgroundImageSrc}');
        background-size: cover;
        background-repeat: no-repeat;
        background-position: center;
        background-origin: border-box;
        background-clip: border-box;
        min-height: 100vh;
      `)
    }

    return styles
  }, [backgroundImageSrc, isSafari])

  let content = null

  if (templateId === GLOBAL_SURNAME_PAGE_ID) {
    const globalSurnameProps = assemblyInstructions[0]?.props || {}
    content = (
      <GlobalSurnamePage
        surname={globalSurnameProps?.surnameId}
        surnamePage={globalSurnameProps?.surnamePage}
        showMap={globalSurnameProps?.surnamePage === '1'}
        pageNumber={pageNumber}
        isPrintView
      />
    )
  } else if (templateId === TREE_DESIGNS_PAGE_ID) {
    content = (
      <TreeDesignsPage pageNumber={pageNumber} memoryId={assemblyInstructions[0]?.props?.memoryId} isPrintView />
    )
  } else if (templateId === CHINESE_SURNAME_PAGE_ID) {
    const surnameId = assemblyInstructions[0]?.props?.surnameId
    if (!surnameId) {
      content = <PlaceholderSurnamePage isGlobalSurnamePage={false} />
    } else {
      content = (
        <ChineseSurnamePage
          surnameId={assemblyInstructions[0]?.props?.surnameId}
          color={assemblyInstructions[0]?.props?.color}
          isPrintView
          pageNumber={pageNumber}
        />
      )
    }
  } else if (FRONT_COVER_PAGE_IDS.includes(templateId)) {
    content = (
      <FrontCoverPage pageNumber={pageNumber} elements={assemblyInstructions[0]} templateId={templateId} isPrintView />
    )
  } else if (templateId === BACK_COVER_PAGE_ID) {
    content = <BackPage pageNumber={pageNumber} elements={assemblyInstructions[0]} isPrintView />
  } else {
    content = (
      <PageAssembler
        assemblyInstructions={assemblyInstructions}
        pageNumber={pageNumber}
        isPrintView
        isSafari={isSafari}
      />
    )
  }

  const sortedCustomLayoutElements = useMemo(() => {
    if (!Array.isArray(customLayoutElements)) return []
    return [...customLayoutElements].sort((a, b) => (a.zindex || 0) - (b.zindex || 0))
  }, [customLayoutElements])

  const absoluteContainerCSS = css`
    position: absolute;
    top: 0;
    inset-inline-start: 0;
    width: 100%;
    height: 100%;
    transform-origin: top left;
    pointer-events: none;

    & > * {
      pointer-events: auto;
    }
  `

  return (
    <div css={pageCSStyles}>
      {content}

      {/* Container for stickers and freeform elements */}
      <div css={absoluteContainerCSS}>
        <PrintStickerLayer stickers={stickers} isSafari={isSafari} />
        {sortedCustomLayoutElements.map((element) => {
          const commonProps = {
            picker: {
              ...element,
              pageNumber,
            },
            key: element.id,
            isPrintView: true,
            zindex: element.zindex,
            isSafari,
          }

          if (element.type === 'freeform-image-picker') {
            return <FreeformImagePicker {...commonProps} />
          }

          if (element.type === 'freeform-text-picker') {
            const textType = element.pickerData?.textType
            // Render FreeformTextBox for caption/title, FreeformTextPicker for story
            if (textType === 'caption' || textType === 'title') {
              return <FreeformTextBox {...commonProps} />
            }
            return <FreeformTextPicker {...commonProps} />
          }

          return null
        })}
      </div>
    </div>
  )
}
export default function PrintingPages({ includeBlankInnerCovers = false }) {
  const isNewPrintFlag = useFeatureFlag('chinese_memoryBooks_newPrint').isOn
  const { memoryBook } = useMemoryBook()
  const contentPages = useMemo(() => memoryBook?.contentPages || [], [memoryBook])

  // Memoize the rearranged pages for print
  const printOrderPages = useMemo(() => {
    if (!contentPages.length) return []

    const filteredPages = contentPages.filter((page) => page.page !== 'back')
    const backPage = contentPages.find((page) => page.page === 'back') || null
    const frontPageIndex = filteredPages.findIndex((page) => page.page === 'front')

    const arrangedPages = [...filteredPages]

    if (frontPageIndex !== -1 && includeBlankInnerCovers) {
      const innerFrontPage = newPageState('inner-front')
      arrangedPages.splice(frontPageIndex + 1, 0, innerFrontPage)
    }

    if (includeBlankInnerCovers) {
      const innerBackPage = newPageState('inner-back')
      return backPage ? [...arrangedPages, innerBackPage, backPage] : arrangedPages
    }

    return backPage ? [...arrangedPages, backPage] : arrangedPages
  }, [contentPages, includeBlankInnerCovers])

  const globalStyles = [baseGlobalPrintStyles, pageSizeStyles]

  return (
    <>
      {!isNewPrintFlag && <Global styles={globalStyles} />}

      <div css={!isNewPrintFlag && pageContainerCSS}>
        {printOrderPages.map((page, index) => (
          <div key={page.id || `${page.page}-${index}`} className="pdf-page">
            <Page
              templateId={page?.templateId}
              assemblyInstructions={page?.elements}
              pageNumber={page.page}
              stickers={page.stickers}
              customLayoutElements={page.customLayoutElements}
              backgroundId={page.backgroundId}
            />

            {index < printOrderPages.length - 1 && !isNewPrintFlag && <div className="page-break" />}
          </div>
        ))}
      </div>
    </>
  )
}
